import { useGetFilesQuery } from "apollo/hooks/queries";
import { useEnvContext } from "context/EnvContext";
import { FileParams, Filev2, QueryProgress } from "core/types";
import { useMemo } from "react";
import {
  useLoggedCareproviderId,
  useLoggedCareseeker,
} from "reduxentities/hooks";

type FilteredFiles = {
  receivedFiles: Filev2[] | null;
  uploadedFiles: Filev2[] | null;
};

export function useGetFilteredFiles({
  params,
}: {
  params: FileParams;
}): [QueryProgress, FilteredFiles] {
  const [filesQueryProgress, files] = useGetFilesQuery({ params });
  const loggedCareseeker = useLoggedCareseeker();
  const loggedCareproviderId = useLoggedCareproviderId();
  const id = loggedCareseeker?.id ?? loggedCareproviderId;
  const { isSenderApp } = useEnvContext();

  const filteredFiles = useMemo<FilteredFiles>(() => {
    if (!id || !files) {
      return {
        receivedFiles: null,
        uploadedFiles: null,
      };
    }

    const result: { receivedFiles: Filev2[]; uploadedFiles: Filev2[] } = {
      receivedFiles: [],
      uploadedFiles: [],
    };

    for (const file of files) {
      if (!file.careseeker?.id && !file.careprovider?.id) {
        continue;
      }
      const isUploaded = isSenderApp
        ? file.careseeker?.id === id
        : file.careprovider?.id === id;

      if (isUploaded) {
        result.uploadedFiles.push(file);
      } else {
        result.receivedFiles.push(file);
      }
    }

    return result;
  }, [files, id, isSenderApp]);

  return [filesQueryProgress, filteredFiles];
}
